<template>
    <section :id="sectionId" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">CellXpert</h2>
        <Accordion :multiple="true" v-model:activeIndex="accordionActive">
            <AccordionTab header="Setting up tracking links">
                <div class="text-sm leading-relaxed">
                    <p class="mb-3">
                        CellXpert works like ReferOn — the raw link comes from the CellXpert admin panel (or
                        directly from the operator), and pasting it into <strong>Affiliate Link</strong> in
                        AffiliationSpace triggers auto-enrichment. You do not add anything by hand.
                    </p>

                    <ol class="list-decimal ml-6 mb-4 space-y-1">
                        <li>Get the raw tracking URL from CellXpert / the operator. It looks like<br />
                            <code class="bg-black/40 px-1 py-0.5 rounded">https://go.afftrackio.com/visit/?bta=35375&amp;nci=5376</code>
                            — the <code>bta</code> is the brand tracking id, the <code>nci</code> is the creative id.</li>
                        <li>Go to <strong>Advertisers</strong> in the sidebar, expand the brand, and find the pending row.</li>
                        <li>Paste the URL into <strong>Affiliate Link</strong>. Do not touch Internal Note. Do not add
                            <code>utm_campaign</code> — see the note below.</li>
                        <li>Save — the row's Link status flips to <span class="text-green-400 font-semibold">linked</span>.</li>
                    </ol>

                    <p class="mb-2 font-medium">Where you paste it</p>
                    <div class="mb-4">
                        <img :src="pasteTargetScreenshot"
                             alt="AffiliationSpace — pending SiteBrandLink row with empty Internal Note and Affiliate Link fields"
                             class="rounded border border-gray-700"
                             style="width: 100%; max-width: 1400px; height: auto;" />
                        <p class="text-xs text-gray-400 mt-1">
                            Same UI as ReferOn (shown here). Paste the operator URL into <strong>Affiliate Link</strong>
                            — Internal Note and the <code>utm_campaign</code> parameter both fill themselves.
                        </p>
                    </div>

                    <p class="mb-1 font-medium">What the enricher does</p>
                    <div class="mb-4 p-3 rounded bg-black/40 text-xs font-mono">
                        <p class="text-gray-400 mb-1">// Raw (from operator)</p>
                        <p class="mb-2">https://go.afftrackio.com/visit/?bta=35375&amp;nci=5376</p>
                        <p class="text-gray-400 mb-1">// Enriched (what actually gets saved)</p>
                        <p class="mb-2">https://go.afftrackio.com/visit/?bta=35375&amp;nci=5376&amp;utm_campaign=<span class="text-green-400">clemexgb</span>&amp;afp1=<span class="text-green-400">{clickid}</span></p>
                        <p class="text-gray-400 mb-1">// Internal Note</p>
                        <p><span class="text-green-400">clemexgb</span></p>
                    </div>

                    <div class="p-3 rounded border border-amber-400/50 bg-amber-50/10 text-xs mb-4">
                        <p class="font-semibold mb-1">Do NOT add a <code>utm_campaign</code> yourself</p>
                        <p>
                            The enricher generates it for you as
                            <code>first3Alpha(advertiserName) + first3Alpha(publisherName) + geo(market)</code> —
                            e.g. <em>Cleanwins</em> × <em>Meximum SAS</em> × <em>GB</em> → <code>clemexgb</code>. That value
                            also becomes the row's <strong>Internal Note</strong>. If you paste a URL that already
                            has a <code>utm_campaign</code>, the enricher keeps what's there — so pasting a
                            hand-edited one will override the auto-code and can collide with another publisher.
                            Safer to paste the URL raw and let the enricher do it.
                        </p>
                    </div>

                    <div class="p-3 rounded border border-blue-400/50 bg-blue-950/30 text-xs">
                        <p class="font-semibold mb-1">Format validation</p>
                        <p>
                            The validator (<code>src/utils/tracking-link-validator.js</code>, <code>cellxpert</code>
                            branch) requires <code>bta</code>, <code>nci</code>, and <code>utm_campaign</code> to be
                            present on save. The first two must come from the operator; the third is what the
                            enricher fills in on paste — if it's missing, the URL didn't parse or the enricher
                            couldn't derive the campaign code. Check the URL shape and try again.
                        </p>
                    </div>
                </div>
            </AccordionTab>
        </Accordion>
    </section>
</template>

<script setup>
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import { useHelpSection } from './useHelpSection';
// Reusing the ReferOn paste-target screenshot — the AffiliationSpace side
// UI is the same across all affiliate systems, so a CellXpert-specific
// screenshot would show identical rows.
import pasteTargetScreenshot from '@/assets/docs/referon-paste-target.png';
const { sectionId, accordionActive } = useHelpSection('links-cellxpert');
</script>

